import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "../client";
import { useApiClient } from "../context";
import type { components } from "../schema";
import {
  diffLevels,
  type PromotedCharacter,
} from "../../features/character/unlockTransitionTracker";

export type CharacterUnlockResponse =
  components["schemas"]["UnlockedCharacterListResponse"];

// useHistory と同 pattern の read-only query に、UnlockToast 用の昇格状態を付与した hook。
// promotedCharacters は最新 fetch 時点で前回 result から昇格があったキャラの集計。
// dismissPromotion は UnlockToast の onDismiss から呼んで state を空に戻す。
export type UseCharacterUnlockResult = UseQueryResult<
  CharacterUnlockResponse,
  ApiError
> & {
  promotedCharacters: PromotedCharacter[];
  dismissPromotion: () => void;
};

export function useCharacterUnlock(
  playlineId: string | null | undefined,
): UseCharacterUnlockResult {
  const client = useApiClient();

  const query = useQuery<CharacterUnlockResponse, ApiError>({
    queryKey: ["character-unlock", playlineId],
    enabled: playlineId !== null && playlineId !== undefined,
    queryFn: async () => {
      // enabled: false の場合は呼ばれないが、型を狭めるための保険的なガード。
      if (playlineId === null || playlineId === undefined) {
        throw new ApiError(0, {
          type: "about:blank",
          title: "Invalid state",
          status: 0,
          detail: "useCharacterUnlock queryFn invoked without playlineId",
        });
      }

      const { data, error } = await client.GET(
        "/playlines/{playlineId}/character-unlock",
        {
          params: { path: { playlineId } },
        },
      );

      if (error !== undefined) {
        throw new ApiError(
          typeof (error as { status?: number }).status === "number"
            ? (error as { status: number }).status
            : 0,
          error as unknown as ApiError["problem"],
        );
      }
      if (data === undefined) {
        throw new ApiError(500, {
          type: "about:blank",
          title: "Empty response",
          status: 500,
          detail: "getCharacterUnlock returned no data",
        });
      }
      return data;
    },
  });

  // 前回 result snapshot。初回 fetch では undefined のまま、
  // diffLevels(undefined, current) が空配列を返すことで toast 抑制 (Risk #3)。
  const lastResultRef = useRef<CharacterUnlockResponse | undefined>(undefined);
  const [promotedCharacters, setPromotedCharacters] = useState<
    PromotedCharacter[]
  >([]);

  const currentData = query.data;
  useEffect(() => {
    if (currentData === undefined) return;
    const promoted = diffLevels(lastResultRef.current, currentData);
    if (promoted.length > 0) {
      setPromotedCharacters(promoted);
    }
    lastResultRef.current = currentData;
  }, [currentData]);

  const dismissPromotion = useCallback(() => {
    setPromotedCharacters([]);
  }, []);

  // useQuery の戻り値に追加 field を載せる。useQuery 戻り値の `as` キャストは避け、
  // spread で安全にマージする。
  return {
    ...query,
    promotedCharacters,
    dismissPromotion,
  } as UseCharacterUnlockResult;
}
